import React, { useRef } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut, getElementAtEvent } from "react-chartjs-2";
import { InvoicesList } from "../Invoices";
import { Accounts } from "../../pages/Accounts/Accounts";
import PropsalList from "../Proposal/PropsalList/PropsalList";
import { useTheme } from "../../context/ThemeContext";

export function DoughnutChart({
  title,
  dataSet,
  setIsVisibleModal,
  setModalTitle,
  setModalContent,
}) {
  ChartJS.register(ArcElement, Tooltip, Legend);
  const { themeMode } = useTheme();
  const isDarkMode = themeMode === "dark";
  const total = (dataSet?.datasets?.[0]?.data || []).reduce(
    (sum, item) => sum + Number(item || 0),
    0,
  );
  const chartData = {
    ...dataSet,
    datasets: (dataSet?.datasets || []).map((dataset) => ({
      ...dataset,
      borderColor: isDarkMode ? "#111827" : "#ffffff",
      borderWidth: 5,
      borderRadius: 8,
      spacing: 3,
      hoverBorderWidth: 3,
      hoverOffset: 10,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    layout: {
      padding: 6,
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 650,
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: isDarkMode ? "#d1d5db" : "#4b5563",
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 8,
          boxHeight: 8,
          font: {
            family: "'Inter', sans-serif",
            size: 12,
            weight: "600",
          },
          padding: 18,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode ? "#111827" : "#ffffff",
        borderColor: isDarkMode ? "#26334a" : "#dbe3ef",
        borderWidth: 1,
        titleColor: isDarkMode ? "#f3f4f6" : "#111827",
        bodyColor: isDarkMode ? "#d1d5db" : "#4b5563",
        cornerRadius: 10,
        padding: 12,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        callbacks: {
          label: (context) => {
            const value = Number(context.raw || 0);
            const percentage = total > 0 ? ` (${Math.round((value / total) * 100)}%)` : "";
            return ` ${context.label}: ${value}${percentage}`;
          },
        },
      },
    },
  };
  const chartRef = useRef();

  const onClick = (event) => {
    const element = getElementAtEvent(chartRef.current, event);
    if (!element.length) return;
    const index = element[0]?.index;
    const datasetIndex = element[0]?.datasetIndex;
    const DataTable = dataSet?.datasets[datasetIndex]?.dataRender[index];

    setIsVisibleModal(true);
    setModalTitle(`${dataSet?.labels[index]}` || "");
    setModalContent(
      <>
        {title === "Invoices (By Status)" && (
          <InvoicesList
            invoices={DataTable}
            title={title}
            pageSize={20}
            chart={true}
          />
        )}
        {title === "PARC Agreement" && (
          <Accounts
            dataChart={DataTable}
            pageSize={20}
            title={"PARC Agreement"}
          />
        )}
        {title === "Proposals (By Status)" && (
          <PropsalList
            proposal={DataTable}
            title={"Proposals"}
            pageSize={20}
            dataChart={true}
          />
        )}
      </>,
    );
  };

  return (
    <div className="chart-card-wrapper">
      <div className="chart-card-header">
        <span className="chart-card-title">{title}</span>
        <span className="chart-card-total">{total}</span>
      </div>
      <div className="chart-canvas-shell">
        <div className="chart-center-metric">
          <span className="chart-center-metric__value">{total}</span>
          <span className="chart-center-metric__label">Total</span>
        </div>
        <Doughnut
          data={chartData}
          options={options}
          redraw={true}
          onClick={onClick}
          ref={chartRef}
        />
      </div>
    </div>
  );
}
